import { useEffect, useState } from "react";

import {
  getFaultRun,
  listFaultRunsForRevision,
  type FaultRunChoice,
  type FaultRunFaultLevel,
} from "../services/faultLink";
import { ApiError } from "../services/http";
import { formatQuantity } from "../utils/formatQuantity";
import { STATUS_LABELS as FAULT_STATUS_LABELS } from "./FaultResultSummary";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";

// The fault-run selector of the studies that link a stored EOS-04 fault level (starter
// coordination, breaker selectivity, busbar evidence; A20 (d), A21 (a), A24 (c)) - one
// component, where the three forms carried a copy each until SLD-b1 (A27 (e)). It lists
// the fault runs of the active revision, drops a chosen run the revision no longer holds,
// reads the chosen run's stored fault level for display only (the backend reads the same
// frozen result again when the study is saved), and says when the run is not a maximum
// three-phase study, when its bus differs from the study's, and - for the studies that use
// the peak current - when the stored result carries no ip.

/** The first option of the selector: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export const NO_OPEN_REVISION_NOTE =
  "Select an open project revision in the topbar to link a fault run, or use a typed value.";

export const NO_FAULT_RUNS_NOTE = "This revision holds no fault run yet.";

export const FAULT_RUN_NOT_LINKABLE =
  "This fault run has no initial short-circuit current and cannot be linked.";

export const NOT_MAXIMUM_THREE_PHASE_NOTE =
  "This fault run is not a maximum three-phase study; the result will be Engineering " +
  "review required.";

/** "The fault run bus <x> differs from the study bus <y>; ..." */
export function busMismatchNote(faultBus: string, studyBus: string): string {
  return (
    `The fault run bus ${faultBus} differs from the study bus ${studyBus}; the result will ` +
    "be Engineering review required."
  );
}

/** The option text of one fault run in the selector: "SC-MSB-01 · rev 3 · Study calculated". */
export function faultRunOptionText(run: FaultRunChoice): string {
  const status =
    (FAULT_STATUS_LABELS as Record<string, string>)[run.design_check_status] ??
    run.design_check_status;
  return `${run.calculation_key} · rev ${run.revision_number} · ${status}`;
}

/**
 * The read-only line under the selector: the fault level as the stored result holds it,
 * with the peak ip for the studies that use it.
 */
export function linkedDetailText(level: FaultRunFaultLevel, withPeak: boolean): string {
  const current = formatQuantity(level.initial_symmetrical_short_circuit_current_ka);
  const words = `${CASE_LABELS[level.calculation_case]} · ${FAULT_TYPE_LABELS[level.fault_type]}`;
  let bus = `Bus ${level.fault_bus_code} · Ik'' ${current.display} kA`;
  if (withPeak) {
    const peak =
      level.peak_short_circuit_current_ka === null
        ? "ip —"
        : `ip ${formatQuantity(level.peak_short_circuit_current_ka).display} kA`;
    bus = `${bus} · ${peak}`;
  }
  return `${bus} · ${words} · ${level.nominal_voltage_v} V`;
}

/**
 * The tooltip of that line: the exact engine decimals - the starter form shows the exact
 * Ik'' alone, the studies that use the peak name both ("Ik'' … · ip …").
 */
export function linkedDetailTitle(level: FaultRunFaultLevel, withPeak: boolean): string {
  const current = formatQuantity(level.initial_symmetrical_short_circuit_current_ka, "kA").exact;
  if (!withPeak) {
    return current ?? "";
  }
  if (level.peak_short_circuit_current_ka === null) {
    return `Ik'' ${current}`;
  }
  const peak = formatQuantity(level.peak_short_circuit_current_ka, "kA").exact;
  return `Ik'' ${current} · ip ${peak}`;
}

export function isMaximumThreePhase(level: FaultRunFaultLevel): boolean {
  return level.calculation_case === "MAXIMUM" && level.fault_type === "THREE_PHASE";
}

type FaultRunSelectorProps = {
  /** The active open revision, or null (then only the note is shown). */
  revisionId: string | null;
  /** The run kept in the draft, "" when none. */
  faultRunId: string;
  onChange: (faultRunId: string) => void;
  /** The study's bus, trimmed; "" while not typed - the mismatch note needs both. */
  busCode: string;
  /** The studies that use the peak current show ip and say when it is missing. */
  withPeak: boolean;
  /** The study's own sentence for a run without ip (the making check, the peak withstand check). */
  noPeakNote?: string;
  /** The study's own sentence for a run that is not the maximum three-phase fault; the
   * default says the result goes to review, which is true of the studies whose engine
   * reads the link. A cable study records it in its fault link instead (A28 (e)). */
  notMaximumNote?: string;
  /** The run the selector read, for a form that shows the figure itself; null when none is
   * chosen or the run cannot be read. */
  onLinked?: (level: FaultRunFaultLevel | null) => void;
  /** The study's own sentence for a run at another bus than the study's; the default says
   * the result goes to review. A power quality study records it in its fault link instead
   * (EOS-11, A32). */
  busMismatchNoteFor?: (faultBus: string, studyBus: string) => string;
  /** Whether a run is the case the study wants; the default is the maximum three-phase
   * fault. The earth-fault loop study (EOS-05d, A33) wants the minimum single-phase-to-earth
   * case and passes its own test with its own note. */
  isExpectedRun?: (level: FaultRunFaultLevel) => boolean;
};

export function FaultRunSelector({
  revisionId,
  faultRunId,
  onChange,
  busCode,
  withPeak,
  noPeakNote,
  notMaximumNote,
  onLinked,
  busMismatchNoteFor = busMismatchNote,
  isExpectedRun = isMaximumThreePhase,
}: FaultRunSelectorProps) {
  // The fault runs of the active revision: null until they are loaded.
  const [faultRuns, setFaultRuns] = useState<FaultRunChoice[] | null>(null);
  const [faultRunsError, setFaultRunsError] = useState<string | null>(null);
  // The chosen run's stored fault level, or why it cannot be shown.
  const [linked, setLinked] = useState<FaultRunFaultLevel | null>(null);
  const [linkedError, setLinkedError] = useState<string | null>(null);

  // The list follows the active revision; a revision without an open state lists nothing.
  useEffect(() => {
    if (revisionId === null) {
      setFaultRuns(null);
      setFaultRunsError(null);
      return;
    }
    const controller = new AbortController();
    setFaultRuns(null);
    setFaultRunsError(null);
    listFaultRunsForRevision(revisionId, controller.signal)
      .then((runs) => {
        if (!controller.signal.aborted) {
          setFaultRuns(runs);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setFaultRunsError(
            error instanceof Error ? error.message : "The fault runs could not be listed.",
          );
        }
      });
    return () => controller.abort();
  }, [revisionId]);

  // A run kept in the draft that the listed revision does not hold is no longer chosen.
  useEffect(() => {
    if (faultRuns !== null && faultRunId !== "" && !faultRuns.some((run) => run.id === faultRunId)) {
      onChange("");
    }
  }, [faultRuns, faultRunId, onChange]);

  // The form that shows the figure itself follows the run the selector read.
  useEffect(() => {
    onLinked?.(linked);
  }, [linked, onLinked]);

  // The chosen run's fault level, read from its stored result for display only.
  useEffect(() => {
    if (faultRunId === "") {
      setLinked(null);
      setLinkedError(null);
      return;
    }
    const controller = new AbortController();
    setLinked(null);
    setLinkedError(null);
    getFaultRun(faultRunId, controller.signal)
      .then((level) => {
        if (!controller.signal.aborted) {
          setLinked(level);
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        // A stored result without Ik'' (or another field the study needs) fails the
        // contract; the server's own words are kept for a run that cannot be read.
        setLinkedError(error instanceof ApiError ? error.message : FAULT_RUN_NOT_LINKABLE);
      });
    return () => controller.abort();
  }, [faultRunId]);

  if (revisionId === null) {
    return <p data-no-revision-note="true">{NO_OPEN_REVISION_NOTE}</p>;
  }

  return (
    <>
      <label>
        Fault run
        <select
          name="faultRunId"
          value={faultRunId}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{CHOOSE}</option>
          {(faultRuns ?? []).map((run) => (
            <option key={run.id} value={run.id}>
              {faultRunOptionText(run)}
            </option>
          ))}
        </select>
      </label>
      {faultRuns !== null && faultRuns.length === 0 ? (
        <p data-no-fault-runs-note="true">{NO_FAULT_RUNS_NOTE}</p>
      ) : null}
      {faultRunsError !== null ? <p role="alert">{faultRunsError}</p> : null}
      {linked !== null ? (
        <p data-linked-fault-level={linked.run_id} title={linkedDetailTitle(linked, withPeak)}>
          {linkedDetailText(linked, withPeak)}
        </p>
      ) : null}
      {linkedError !== null ? <p role="alert">{linkedError}</p> : null}
      {withPeak && noPeakNote !== undefined && linked !== null &&
      linked.peak_short_circuit_current_ka === null ? (
        <p data-no-peak-note="true">{noPeakNote}</p>
      ) : null}
      {linked !== null && busCode !== "" && linked.fault_bus_code !== busCode ? (
        <p data-bus-mismatch-note="true">{busMismatchNoteFor(linked.fault_bus_code, busCode)}</p>
      ) : null}
      {linked !== null && !isExpectedRun(linked) ? (
        <p data-not-maximum-note="true">{notMaximumNote ?? NOT_MAXIMUM_THREE_PHASE_NOTE}</p>
      ) : null}
    </>
  );
}

export default FaultRunSelector;
